import * as React from 'react';
import {useSelector} from 'react-redux';

import {Products} from 'nutshell-core/products';
import type {ProductId, Market} from 'nutshell-core/types';

import {ReduxSelectProducts} from '../../../../../ui/core/select/redux-select-products';

import {CancelButton} from '../button/cancel-button/cancel-button';
import {TextField} from '../textfield/textfield';

import styles from './product-picker.module.css';

type Props = {
    input: {
        value: any | null | undefined;
        onChange: (productMap: any) => any;
    };
    selectedOptions: any[];
    onRemove: () => any;
    currentMarket: Market | null | undefined;
};

export function ReduxFormProductPicker(props: Props) {
    const {currentMarket, input} = props;
    const products = useSelector((state) => Products.getAll(state));

    const [productId, setProductId] = React.useState<ProductId | null | undefined>(
        (input.value && input.value.links && input.value.links.product) || null
    );
    const [quantity, setQuantity] = React.useState<string>(
        (input.value && input.value.quantity) || ''
    );
    const [price, setPrice] = React.useState<string>((input.value && input.value.price) || '');

    const getCurrencySymbol = () => {
        return currentMarket ? currentMarket.currencySymbol : '';
    };

    // if market changes, update the price to reflect the new market
    React.useEffect(() => {
        if (currentMarket) {
            // @ts-expect-error
            const selectedProduct = products.find((item) => item.id === productId);
            const marketPrice = selectedProduct && selectedProduct.prices[currentMarket.id];
            const newPrice = marketPrice || {amount: 0};

            handleChangePrice(newPrice.amount.toString());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMarket]);

    React.useEffect(() => {
        if (productId) {
            handleSave();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId, quantity, price]);

    const handleClearProduct = () => {
        setProductId(null);
        setQuantity('');
        setPrice('');

        if (input.value) {
            props.onRemove();
        }
    };

    // @ts-expect-error
    const handleKeyDownProductInputs = (event) => {
        // Capture the ENTER key to confirm the product and not submit the form.
        if (event.which === 13) {
            event.preventDefault();
            handleSave();
        }

        // Capture the ESC key to cancel the product picker.
        if (event.which === 27) {
            event.preventDefault();
            handleClearProduct();
        }
    };

    // @ts-expect-error
    const handleSelectProduct = (value) => {
        const existingProductMap = typeof input.value === 'object' ? input.value : undefined;

        // @ts-expect-error
        const selectedProduct = products.find((item) => item.id === value.value);

        let productPrice;
        if (existingProductMap) {
            productPrice = existingProductMap.price.amount;
        }

        if (selectedProduct && currentMarket) {
            const marketPrice = selectedProduct.prices[currentMarket.id];
            productPrice = marketPrice ? marketPrice.amount : '0';
        } else if (selectedProduct) {
            productPrice = '0';
        }

        setProductId(value.value);
        setPrice(productPrice || '0');
        setQuantity(existingProductMap ? existingProductMap.quantity : '1');
    };

    const handleChangeQuantity = (newQuantity: string) => {
        setQuantity(newQuantity);
    };

    const handleBlurQuantity = () => {
        if (quantity === '0') {
            handleClearProduct();
        } else if (quantity === '') {
            setQuantity('1');
        }
    };

    const handleChangePrice = (newPrice: string) => {
        setPrice(newPrice);
    };

    const handleBlurPrice = () => {
        const formattedPrice = Number(price).toLocaleString(undefined, {
            minimumFractionDigits: 2,
        });

        setPrice(formattedPrice);
    };

    const handleSave = () => {
        const existingProductMap = typeof input.value === 'object' ? input.value : undefined;

        const amount =
            price && typeof price === 'string' ? parseFloat(price.replace(/,/g, '')) : 0.0;
        const productPrice = {
            amount,
            formatted: `${getCurrencySymbol()}${price}`,
        } as const;

        // Round to 2 decimal places — backend column is DECIMAL(18,2) (ESC-360).
        const productQuantity = Math.round(Number(quantity || '1') * 100) / 100;

        if (existingProductMap) {
            const updatedProductMap = {
                ...existingProductMap,
                quantity: productQuantity,
                price: productPrice,
            } as const;

            input.onChange(updatedProductMap);
        } else {
            // @ts-expect-error
            const product = products.find((item) => item.id === productId);

            if (!product) return;

            input.onChange({
                type: 'productMaps',
                name: product.name,
                quantity: productQuantity,
                price: price,
                links: {
                    product: productId,
                },
            });
        }
    };

    // filter out already selected products from the list of available products
    const excludeProductIds = props.selectedOptions
        .filter((field) => field && field.links && field.links.product)
        .map((field) => field.links.product);

    return (
        <div className={styles.container}>
            <div className={styles['select-container']}>
                <ReduxSelectProducts
                    selectedId={productId}
                    onChange={handleSelectProduct}
                    currentMarket={currentMarket}
                    excludeProductIds={excludeProductIds}
                    placeholder='Select a product…'
                    defaultMenuIsOpen={false}
                    maxMenuHeight={200}
                    menuPortalTarget={document.body}
                />
            </div>
            <label className={styles['product-picker-label']}>
                Qty:
                <TextField
                    // @ts-expect-error
                    name='quantity'
                    disabled={!productId}
                    min='0'
                    step='0.01'
                    inputMode='decimal'
                    type='number'
                    value={quantity}
                    onChange={handleChangeQuantity}
                    onBlur={handleBlurQuantity}
                    onKeyDown={handleKeyDownProductInputs}
                    selectOnFocus={true}
                />
            </label>
            <label className={styles['product-picker-label--price']}>
                Price:
                <TextField
                    // @ts-expect-error
                    disabled={!productId}
                    value={price}
                    onChange={handleChangePrice}
                    onBlur={handleBlurPrice}
                    onKeyDown={handleKeyDownProductInputs}
                    selectOnFocus={true}
                />
            </label>
            <div className={styles['cancel-button']}>
                {productId && <CancelButton onClick={handleClearProduct} />}
            </div>
        </div>
    );
}
